import React from 'react';
import type { BBCommonProps } from 'types/story';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import Swiper from 'swiper';
import type { Swiper as SwiperType } from 'swiper/types';
import {
	Navigation,
	Pagination,
	Autoplay,
	Mousewheel,
	EffectFade,
	EffectFlip,
	EffectCoverflow,
	Virtual,
	FreeMode,
} from 'swiper/modules';
import shuffle from 'lodash/shuffle';

import { attachRef } from 'utils/helpers';
import { useClientSelector } from 'client/reducers';
import { selectEditableElementNodeId } from 'client/reducers/editor/selectors';
import { SelectionHintEvEm } from 'client/components/common/SelectionHint/utils';
import { getElementIdByNodeId } from 'client/components/common/BuildingBlocks/utils/common';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { AnimationEvEm } from 'client/components/common/BuildingBlocks/animation';
import { COMPONENT_TYPE } from 'common/constants';
import { useWillUnmount } from 'common/hooks/use-will-unmount';
import Context from '../context';
import SliderSlidesContext from './SliderSlidesContext';
import css from './SliderSlides.scss';
import {
	type SlideDescriptor,
	type VirtualRenderData,
	getVirtualSlidesToRender,
	isVirtualEnabled,
	updateOnVirtualData,
} from '../sliderVirtualUtils';

const isChildHidden = (child: React.ReactElement<BBCommonProps>) =>
	child.props.uiConfig.componentProps?.styles?.display === 'none';

const SliderSlides: React.FC<BBCommonProps> = props => {
	const { settings, paginationSettings, paginationRef, prevArrowRef, nextArrowRef } = React.useContext(Context);
	const { editableModeProps: EMP, stateAttrs, uiConfig, isEditableMode = false, children } = props;
	const swiperRef = React.useRef<Swiper | null>(null);
	const swiperContainerRef = React.useRef<HTMLDivElement | null>(null);
	const swiperSlidesRef = React.useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
	const selectedElementNodeId = useClientSelector(selectEditableElementNodeId);

	const [isInitialized, setIsInitialized] = React.useState(false);
	const [pendingSlidesCount, setPendingSlidesCount] = React.useState(0);
	const [dynamicSlideDescriptors, setDynamicSlideDescriptors] = React.useState<SlideDescriptor[]>([]);
	const [virtualData, setVirtualData] = React.useState<VirtualRenderData | null>(null);
	const pendingSlidesRef = React.useRef(new Set<string>());
	const pendingSlidesTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
	const setVirtualDataRef = React.useRef<(data: VirtualRenderData) => void>(() => undefined);
	setVirtualDataRef.current = setVirtualData;

	const useVirtualSlides = isVirtualEnabled(settings.virtual, isEditableMode);

	const shouldShuffle = !props.isEditableMode && settings.shuffle;
	const [order] = React.useState(() =>
		shuffle(Array.from({ length: React.Children.count(children) }).map((_, index) => index))
	);
	const __children__ = React.useMemo(() => {
		const childrenArray = React.Children.toArray(children);
		return shouldShuffle ? order.map(orderIndex => childrenArray[orderIndex]) : React.Children.toArray(children);
	}, [children, shouldShuffle, order]);

	// store children string to reflect on changes made in children count or order
	const childrenIdString = __children__
		.map(child => (React.isValidElement<BBCommonProps>(child) && !isChildHidden(child) ? child.props._id : null))
		.join(',');

	const staticSlideDescriptors = React.useMemo(() => {
		const descriptors: SlideDescriptor[] = [];
		__children__.forEach(slide => {
			if (!React.isValidElement<BBCommonProps>(slide) || isChildHidden(slide)) return;
			if (slide.props.type === COMPONENT_TYPE.CMS_REPEATER && !isEditableMode) return;
			descriptors.push({ key: `slide-${slide.props._id}`, content: slide });
		});
		return descriptors;
	}, [__children__, isEditableMode]);

	const slideDescriptors = React.useMemo(
		() => [...staticSlideDescriptors, ...dynamicSlideDescriptors],
		[staticSlideDescriptors, dynamicSlideDescriptors]
	);
	const slideDescriptorsRef = React.useRef(slideDescriptors);
	slideDescriptorsRef.current = slideDescriptors;

	React.useEffect(() => {
		setDynamicSlideDescriptors([]);
		pendingSlidesRef.current.clear();
		setVirtualData(null);
	}, [childrenIdString]);

	// Schedule pending slides count update to prevent frequent updates
	// This is particularly crucial for handling slides rendered with dynamic content, such as CmsRepeater
	// Ensures the count is updated after all slides, including CmsRepeater slides, are fully rendered
	const schedulePendingSlidesCount = React.useCallback(() => {
		if (isEditableMode) return;

		if (pendingSlidesTimeoutRef.current) {
			clearTimeout(pendingSlidesTimeoutRef.current);
		}

		pendingSlidesTimeoutRef.current = setTimeout(() => {
			setPendingSlidesCount(pendingSlidesRef.current.size);
		}, 250);
	}, [isEditableMode]);

	const registerDynamicVirtualSlide = React.useCallback((content: React.ReactNode, key: string) => {
		setDynamicSlideDescriptors(prev => {
			if (prev.some(descriptor => descriptor.key === key)) return prev;
			return [...prev, { key, content }];
		});
	}, []);

	// Function to render each slide
	// `isPending` is used to determine if the slide is still loading, which is relevant for CmsRepeater
	const renderSlide = React.useCallback(
		(content: React.ReactNode, key: string, isPending: boolean = true) => {
			if (useVirtualSlides) {
				registerDynamicVirtualSlide(content, key);
				if (isPending) {
					pendingSlidesRef.current.add(key);
					schedulePendingSlidesCount();
				}
				return null;
			}

			swiperSlidesRef.current[key] = React.createRef<HTMLDivElement>();

			if (isPending) {
				pendingSlidesRef.current.add(key);
				schedulePendingSlidesCount();
			}

			return (
				<div key={key} ref={swiperSlidesRef.current[key]} className={cn(css.slide, 'swiper-slide')}>
					{content}
				</div>
			);
		},
		[useVirtualSlides, registerDynamicVirtualSlide, schedulePendingSlidesCount]
	);

	const sliderContextValue = React.useMemo(() => ({ renderSlide }), [renderSlide]);

	const initializeSwiper = React.useCallback(() => {
		if (!swiperContainerRef.current) return;
		const virtualSettings = useVirtualSlides
			? {
					enabled: true,
					slides: slideDescriptorsRef.current,
					cache: false,
					renderExternal: (data: VirtualRenderData) => setVirtualDataRef.current(data),
					renderExternalUpdate: false,
				}
			: settings.virtual;

		swiperRef.current = new Swiper(swiperContainerRef.current, {
			...settings,
			virtual: virtualSettings,
			navigation: { nextEl: nextArrowRef.current, prevEl: prevArrowRef.current },
			pagination: {
				el: paginationRef.current,
				clickable: true,
				...(typeof paginationSettings === 'object' ? paginationSettings : null),
			},
			lazyPreloadPrevNext: 1,
			mousewheel: {
				enabled: Boolean(settings.mousewheel),
				// preserve double swipe trigger using trackpad or mousewheel
				thresholdDelta: 20,
				releaseOnEdges: Boolean(settings.touchReleaseOnEdges),
			},

			modules: [
				Navigation,
				Pagination,
				Autoplay,
				Mousewheel,
				Virtual,
				FreeMode,
				EffectFade,
				EffectFlip,
				EffectCoverflow,
			],
		});
	}, [settings, paginationSettings, paginationRef, prevArrowRef, nextArrowRef, useVirtualSlides]);

	const swiperInitRevision = useVirtualSlides ? childrenIdString : `${childrenIdString}:${pendingSlidesCount}`;

	React.useEffect(
		function init() {
			initializeSwiper();

			setIsInitialized(true);
			return () => {
				swiperRef.current?.destroy();
				setIsInitialized(false);
			};
		},
		[initializeSwiper, swiperInitRevision]
	);

	React.useLayoutEffect(() => {
		if (!useVirtualSlides) return;
		updateOnVirtualData(swiperRef.current);
	}, [virtualData, useVirtualSlides]);

	React.useEffect(() => {
		const swiper = swiperRef.current;
		if (!useVirtualSlides || !isInitialized || !swiper || swiper.destroyed) return;
		swiper.virtual.slides = slideDescriptors;
		swiper.virtual.update(true);
	}, [slideDescriptors, useVirtualSlides, isInitialized]);

	useTriggerInViewAnimation({
		isInitialized,
		settings,
		isEditableMode,
		swiperRef,
		deps: { initializeSwiper, swiperInitRevision },
	});

	const isMultipleSlides = settings.effect === 'slide' && Number(settings.slidesPerView) > 1;

	React.useEffect(
		function slideToSelected() {
			const selectedElement =
				isEditableMode && selectedElementNodeId ? document.getElementById(selectedElementNodeId) : null;

			if (selectedElement && swiperContainerRef.current?.contains(selectedElement)) {
				const slidesArray = Array.from(swiperRef.current?.slides || []) as HTMLDivElement[];
				const idx = slidesArray.findIndex(slide => slide.contains(selectedElement));

				const matchSlide = idx !== -1;
				const shouldSlide = isMultipleSlides
					? matchSlide && !isSlideInView(swiperContainerRef.current, selectedElement)
					: matchSlide;

				if (shouldSlide) {
					swiperRef.current?.enable();
					swiperRef.current?.slideTo(idx, 0, false);
					swiperRef.current?.disable();
					SelectionHintEvEm.emit('forceUpdate');
				}
			}
		},
		[isEditableMode, selectedElementNodeId, isMultipleSlides]
	);

	useWillUnmount(() => {
		if (pendingSlidesTimeoutRef.current) clearTimeout(pendingSlidesTimeoutRef.current);
	});

	const virtualSlideStyle = React.useMemo((): React.CSSProperties => {
		if (!virtualData || !swiperRef.current) return {};
		const { offset } = virtualData;
		const swiper = swiperRef.current;
		return swiper.isHorizontal()
			? { [swiper.rtlTranslate ? 'right' : 'left']: `${offset}px` }
			: { top: `${offset}px` };
	}, [virtualData]);

	const virtualSlidesToRender = React.useMemo(() => {
		if (!useVirtualSlides || !virtualData) return [];
		return getVirtualSlidesToRender(slideDescriptors, virtualData, Boolean(settings.loop));
	}, [useVirtualSlides, virtualData, slideDescriptors, settings.loop]);

	return (
		<div
			{...uiConfig.nodeProps}
			{...stateAttrs}
			{...props.eventListeners}
			{...EMP?.nodeProps}
			style={uiConfig.nodeProps?.style}
			className={cn(css.slides, uiConfig.nodeProps.className, EMP?.nodeProps?.className, 'swiper-container')}
			ref={attachRef([props.containerRef!, swiperContainerRef])}
		>
			<div className={cn('swiper-wrapper')}>
				{useVirtualSlides
					? virtualSlidesToRender.map(({ descriptor, virtualIndex }) => {
							return (
								<div
									key={descriptor.key}
									className={cn(css.slide, 'swiper-slide')}
									data-swiper-slide-index={virtualIndex}
									style={virtualSlideStyle}
								>
									{descriptor.content}
								</div>
							);
						})
					: null}
				{__children__.map(slide => {
					if (React.isValidElement<BBCommonProps>(slide)) {
						if (isChildHidden(slide)) {
							// render to portal in edit mode to keep the slide in the DOM to show/hide from Layers
							return isEditableMode ? createPortal(<span>{slide}</span>, document.body) : null;
						}

						const key = `slide-${slide.props._id}`;

						// Render each CmsRepeater replica properly, in a slide container.
						if (slide.props.type === COMPONENT_TYPE.CMS_REPEATER && !isEditableMode) {
							return (
								<SliderSlidesContext.Provider value={sliderContextValue} key={key}>
									{slide}
								</SliderSlidesContext.Provider>
							);
						}

						if (useVirtualSlides) return null;

						return renderSlide(slide, key, false);
					}

					return null;
				})}
			</div>
		</div>
	);
};

function isSlideInView(container: HTMLElement, element: HTMLElement) {
	const containerRect = container.getBoundingClientRect();
	const elementRect = element.getBoundingClientRect();

	return (
		elementRect.top >= containerRect.top &&
		elementRect.bottom <= containerRect.bottom &&
		elementRect.left >= containerRect.left &&
		elementRect.right <= containerRect.right
	);
}

interface UseTriggerInViewAnimationParams {
	isInitialized: boolean;
	settings: SwiperType['params'];
	isEditableMode: boolean;
	swiperRef: React.MutableRefObject<Swiper | null>;
	deps?: { initializeSwiper: unknown; swiperInitRevision: string };
}

function useTriggerInViewAnimation(props: UseTriggerInViewAnimationParams) {
	const { isInitialized, isEditableMode, swiperRef, settings } = props;

	React.useEffect(() => {
		const prevInView = new Set<string>();

		function getSlidesPerView(swiper: SwiperType): number {
			return settings.slidesPerView === 'auto' ? swiper.slidesPerViewDynamic() : settings.slidesPerView ?? 1;
		}

		function getSlideIndex(slide: HTMLElement, fallbackIndex = -1): number {
			return parseInt(slide.dataset.swiperSlideIndex ?? fallbackIndex.toString(), 10);
		}

		/**
		 * Get sorted slides from Swiper instance
		 *
		 * Notes
		 * - `props.children` is not used here because if CMS_REPEATER is used as a slide,
		 * it can be displayed dynamically and `props.children` will not contain generated child elements,
		 * so DOM must be used to obtain slides universally.
		 * - Use `data-swiper-slide-index` to sort slides, because Swiper does not guarantee the order of the slides
		 * in the DOM when using lazy|virtual slides.
		 */
		function getSortedSlides(swiper: SwiperType): HTMLElement[] {
			const slides = Array.from(swiper.slides);
			const hasDataSwiperSlideIndex = slides.some(slide => slide.dataset.swiperSlideIndex !== undefined);
			if (hasDataSwiperSlideIndex) {
				return slides.sort((a, b) => getSlideIndex(a) - getSlideIndex(b));
			}
			return slides;
		}

		function getSlideBBElement(slide: HTMLElement): HTMLDivElement | null {
			return slide.querySelector<HTMLDivElement>('[data-bb]');
		}

		function getSlideBBElementId(slide: HTMLElement): string | undefined {
			const slideElement = getSlideBBElement(slide);
			return slideElement?.id ? getElementIdByNodeId(slideElement.id) : undefined;
		}

		function emitSlideAnimationPlayback(bbElement: HTMLDivElement) {
			const slideElementId = getElementIdByNodeId(bbElement.id);
			if (slideElementId) {
				AnimationEvEm.emit('playback', { action: 'onSlideChangeStart', _id: slideElementId });

				const childBlocks = bbElement.querySelectorAll<HTMLDivElement>('[data-bb]');
				childBlocks.forEach(child => {
					const childId = getElementIdByNodeId(child.id);
					if (childId) AnimationEvEm.emit('playback', { action: 'onSlideChangeStart', _id: childId });
				});
			}
		}

		function onInit(swiper: SwiperType) {
			prevInView.clear();

			const slidesPerView = getSlidesPerView(swiper);
			const slides = getSortedSlides(swiper).slice(0, slidesPerView);

			slides.forEach(slide => {
				const bbElement = getSlideBBElement(slide);
				const slideElementId = getSlideBBElementId(slide);
				if (bbElement) emitSlideAnimationPlayback(bbElement);
				if (slideElementId) prevInView.add(slideElementId);
			});
		}

		function onRealIndexChange(swiper: SwiperType) {
			const startIndex = Number.isInteger(swiper.realIndex) ? swiper.realIndex : 0;
			const slidesPerView = getSlidesPerView(swiper);
			const slides = getSortedSlides(swiper);
			const visibleSlides = new Set<string>();
			const totalSlides = slides.length;
			let slidesProcessed = 0;

			while (slidesProcessed < slidesPerView) {
				let index = startIndex + slidesProcessed;

				// If loop mode is enabled and we exceed total, wrap around to the beginning
				if (index >= totalSlides) {
					index %= totalSlides;
				}

				/**
				 * Find the active slide by index:
				 * - Find the slide with matching `data-swiper-slide-index`
				 * - With lazy loading, the slide might not exist yet - that's expected, we'll skip it
				 * - Fallback to `slideIndex` if `data-swiper-slide-index` is not set (e.g. !loop && !virtual)
				 */
				const activeSlide = slides.find((slide, slideIdx) => getSlideIndex(slide, slideIdx) === index);
				const slideBBElement = activeSlide ? getSlideBBElement(activeSlide) : undefined;
				const slideBBElementId = activeSlide ? getSlideBBElementId(activeSlide) : undefined;

				if (slideBBElement && slideBBElementId) {
					if (!prevInView.has(slideBBElementId)) {
						emitSlideAnimationPlayback(slideBBElement);
					}

					visibleSlides.add(slideBBElementId);
				}

				slidesProcessed += 1;
			}

			prevInView.clear();
			visibleSlides.forEach(slideBBElementId => prevInView.add(slideBBElementId));
		}

		// Add event listeners to the swiper instance
		const swiper = swiperRef.current;
		if (!isEditableMode && swiper) {
			if ('initialized' in swiper && swiper.initialized) onInit(swiper);
			else swiper.on('init', onInit);

			swiper.on('realIndexChange', onRealIndexChange);
		}

		return () => {
			swiper?.off('init', onInit);
			swiper?.off('realIndexChange', onRealIndexChange);
		};
	}, [
		isInitialized,
		settings.slidesPerView,
		isEditableMode,
		swiperRef,
		props.deps?.initializeSwiper,
		props.deps?.swiperInitRevision,
	]);

	return null;
}

export default withCardTransitionContext(SliderSlides);
